/**
 * Conversion utilities between SlackMessageStruct ({template, variables}) and Lexical editor state.
 *
 */

import type { EditorState, SerializedEditorState, SerializedLexicalNode } from 'lexical';
import type { SerializedDynamicTagNode, DynamicTagType } from './DynamicTagNode';
import { $isDynamicTagNode } from './DynamicTagNode';
import { $getRoot, $isElementNode } from 'lexical';
import { ASSIGNMENT_FIELD_WRAPPER } from '../../../types/serverImportedTypes';
import { getAssignmentFieldLabel } from '../slack/assignmentFieldOptions';

// Regex to split on {{{N}}} placeholders and ^^^value^^^ assignment fields
const TEMPLATE_SPLIT_REGEX = /({{{\d+}}}|\^{3}[^^]+\^{3})/g;

/**
 * Convert a SlackMessageStruct into a Lexical SerializedEditorState.
 *
 * @param messageStruct - The old {template, variables} format
 * @param fieldLabelResolver - Function to resolve field IDs to display labels (e.g. "Account.Name")
 * @returns A SerializedEditorState that can be loaded via editor.parseEditorState()
 */
export function messageStructToLexicalState(
  messageStruct: SlackMessageStruct | undefined,
  fieldLabelResolver?: (fieldIds: string[], objectType?: string) => string,
): SerializedEditorState {
  const template = messageStruct?.template ?? '';
  const variables = messageStruct?.variables ?? [];

  const lines = template.split('\n');

  const paragraphs: SerializedLexicalNode[] = lines.map((line) => {
    const children = parseLine(line, variables, fieldLabelResolver);
    return {
      type: 'paragraph',
      direction: null,
      format: '',
      indent: 0,
      version: 1,
      children,
      textFormat: 0,
      textStyle: '',
    } as SerializedLexicalNode;
  });

  // If no lines, create one empty paragraph
  if (paragraphs.length === 0) {
    paragraphs.push({
      type: 'paragraph',
      direction: null,
      format: '',
      indent: 0,
      version: 1,
      children: [],
      textFormat: 0,
      textStyle: '',
    } as SerializedLexicalNode);
  }

  return {
    root: {
      type: 'root',
      direction: null,
      format: '',
      indent: 0,
      version: 1,
      children: paragraphs,
    },
  };
}

/**
 * Parse a single line of template text into Lexical child nodes.
 */
function parseLine(
  line: string,
  variables: SlackFieldsStruct[],
  fieldLabelResolver?: (fieldIds: string[], objectType?: string) => string,
): SerializedLexicalNode[] {
  const children: SerializedLexicalNode[] = [];

  // Split on {{{N}}} and ^^^value^^^ patterns
  const parts = line.split(TEMPLATE_SPLIT_REGEX);

  for (const part of parts) {
    if (!part) continue;

    if (part.startsWith('{{{') && part.endsWith('}}}')) {
      // Dynamic field tag: {{{N}}}
      const indexStr = part.match(/\d+/)?.[0];
      if (indexStr !== undefined) {
        const index = parseInt(indexStr, 10);
        const variable = variables[index];
        if (variable) {
          const fieldIds = variable.fieldIds;
          const fieldContext = variable.fieldContext;
          const isPriorValue = fieldContext === 'PRIOR_VALUE';

          // Resolve label from field IDs
          let label: string;
          if (fieldLabelResolver) {
            const resolvedLabel = fieldLabelResolver(fieldIds);
            label = isPriorValue ? `Prior value of ${resolvedLabel}` : resolvedLabel;
          } else {
            // Fallback: use last field ID segment as label
            const fallbackLabel = fieldIds[fieldIds.length - 1] ?? 'Field';
            label = isPriorValue ? `Prior value of ${fallbackLabel}` : fallbackLabel;
          }

          children.push(
            createSerializedDynamicTag({
              tagType: 'field',
              fieldIds,
              fieldContext,
              label,
            }),
          );
        }
      }
    } else if (part.startsWith('^^^') && part.endsWith('^^^')) {
      // Assignment field: ^^^value^^^
      const fieldValue = part.slice(3, -3);
      const fieldLabel = getAssignmentFieldLabel(fieldValue);

      children.push(
        createSerializedDynamicTag({
          tagType: 'assignment',
          value: fieldValue,
          label: fieldLabel,
        }),
      );
    } else {
      // Plain text
      if (part.length > 0) {
        children.push({
          type: 'text',
          detail: 0,
          format: 0,
          mode: 'normal',
          style: '',
          text: part,
          version: 1,
        } as SerializedLexicalNode);
      }
    }
  }

  return children;
}

/**
 * Create a serialized DynamicTagNode for inclusion in a SerializedEditorState.
 */
function createSerializedDynamicTag(data: {
  tagType: DynamicTagType;
  fieldIds?: string[];
  fieldContext?: string;
  value?: string;
  label: string;
}): SerializedDynamicTagNode {
  return {
    type: 'dynamic-tag',
    detail: 0,
    format: 0,
    mode: 'token',
    style: '',
    text: data.label,
    version: 1,
    tagType: data.tagType,
    fieldIds: data.fieldIds,
    fieldContext: data.fieldContext,
    value: data.value,
    label: data.label,
  };
}

/**
 * Convert a Lexical EditorState back to SlackMessageStruct.
 *
 * Walks the node tree:
 * - TextNodes become template text
 * - DynamicTagNodes with tagType='field' become {{{N}}} placeholders
 * - DynamicTagNodes with tagType='assignment' become ^^^value^^^ markers
 * - Paragraph breaks become \n
 *
 * @param editorState - The Lexical editor state
 * @returns SlackMessageStruct in the old {template, variables} format
 */
export function lexicalStateToMessageStruct(editorState: EditorState): SlackMessageStruct {
  let template = '';
  const variables: SlackFieldsStruct[] = [];

  editorState.read(() => {
    const root = $getRoot();
    const paragraphs = root.getChildren();

    paragraphs.forEach((paragraph, paragraphIndex) => {
      if (paragraphIndex > 0) {
        template += '\n';
      }

      if (!$isElementNode(paragraph)) return;
      const children = paragraph.getChildren();
      children.forEach((child) => {
        if ($isDynamicTagNode(child)) {
          const tagType = child.getTagType();
          if (tagType === 'field') {
            // Field tag -> {{{N}}} placeholder
            const fieldIds = child.getFieldIds();
            const fieldContext = child.getFieldContext();
            if (fieldIds) {
              const index = variables.length;
              const fieldStruct: SlackFieldsStruct = { fieldIds };
              if (fieldContext) {
                fieldStruct.fieldContext = fieldContext as FieldContext;
              }
              variables.push(fieldStruct);
              template += `{{{${index}}}}`;
            }
          } else if (tagType === 'assignment') {
            // Assignment field -> ^^^value^^^
            const value = child.getValue();
            if (value) {
              template += `${ASSIGNMENT_FIELD_WRAPPER}${value}${ASSIGNMENT_FIELD_WRAPPER}`;
            }
          }
        } else {
          // Regular text node
          template += child.getTextContent();
        }
      });
    });
  });

  return { template, variables };
}
